import React from "react";
import { Link } from "react-router-dom";
import { LogOut, Settings, Inbox, Mail, FileText, RefreshCcw, LayoutDashboard, Save, Trash2, Plus, ExternalLink, DollarSign, CreditCard } from "lucide-react";
import { useAdminAuth } from "./auth";
import { useSiteContent } from "./SiteContentContext";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "content", label: "Site Content", icon: FileText },
  { id: "payments-config", label: "Payment Settings", icon: CreditCard },
  { id: "services", label: "Services", icon: Settings },
  { id: "faqs", label: "FAQs", icon: FileText },
  { id: "testimonials", label: "Testimonials", icon: FileText },
  { id: "roles", label: "Open Roles", icon: Settings },
  { id: "bookings", label: "Bookings", icon: Inbox },
  { id: "payments", label: "Payments", icon: DollarSign },
  { id: "subscribers", label: "Subscribers", icon: Mail },
];

function useBookings() {
  const [list, setList] = React.useState([]);
  const refresh = React.useCallback(() => {
    try { setList(JSON.parse(localStorage.getItem("3e_bookings") || "[]")); } catch { setList([]); }
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);
  const remove = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    localStorage.setItem("3e_bookings", JSON.stringify(next));
    setList(next);
  };
  const clear = () => { localStorage.removeItem("3e_bookings"); setList([]); };
  return { list, refresh, remove, clear };
}

function useSubscribers() {
  const [list, setList] = React.useState([]);
  const refresh = React.useCallback(() => {
    try {
      const stored = localStorage.getItem("3e_newsletter_list");
      if (stored) setList(JSON.parse(stored));
      else {
        const single = localStorage.getItem("3e_newsletter");
        setList(single ? [{ email: single, at: Date.now() }] : []);
      }
    } catch { setList([]); }
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);
  const remove = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    localStorage.setItem("3e_newsletter_list", JSON.stringify(next));
    setList(next);
  };
  const clear = () => { localStorage.removeItem("3e_newsletter_list"); localStorage.removeItem("3e_newsletter"); setList([]); };
  return { list, refresh, remove, clear };
}

export default function AdminDashboard() {
  const { logout } = useAdminAuth();
  const { content, update, reset } = useSiteContent();
  const [tab, setTab] = React.useState("overview");
  const [savedPing, setSavedPing] = React.useState(false);

  const ping = () => { setSavedPing(true); setTimeout(() => setSavedPing(false), 1500); };

  const bookings = useBookings();
  const subs = useSubscribers();

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <span className="kicker">Owner workspace</span>
          <h1 className="font-serif-display text-white text-3xl md:text-4xl mt-2">Admin dashboard</h1>
          <p className="text-slate-400 text-sm mt-1">Manage website content, review bookings, and track subscribers.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/" className="btn-ghost"><ExternalLink className="w-4 h-4" /> View site</Link>
          <button onClick={logout} className="btn-ghost"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[220px,1fr] gap-6">
        <aside className="card-soft p-3 h-max lg:sticky lg:top-24">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition ${active ? "bg-[#c7e34a] text-[#0b1220] font-semibold" : "text-slate-300 hover:bg-white/5"}`}>
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </nav>
          <div className="section-divider my-3" />
          <button onClick={() => { if (confirm('Reset all content to defaults? This cannot be undone.')) reset(); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-red-300 hover:bg-red-500/10 w-full">
            <RefreshCcw className="w-3.5 h-3.5" /> Reset to defaults
          </button>
        </aside>

        <section>
          {savedPing && (
            <div className="mb-4 inline-flex items-center gap-2 text-sm text-[#c7e34a] bg-[#c7e34a]/10 border border-[#c7e34a]/30 rounded-full px-3 py-1.5">
              <Save className="w-3.5 h-3.5" /> Saved
            </div>
          )}

          {tab === "overview" && (
            <OverviewPanel content={content} bookings={bookings.list} subs={subs.list} setTab={setTab} />
          )}
          {tab === "content" && (
            <SiteInfoEditor content={content} onChange={(partial) => { update(partial); ping(); }} />
          )}
          {tab === "services" && (
            <ListEditor title="Services" fields={[
              { key: "num", label: "Number" },
              { key: "title", label: "Title" },
              { key: "price", label: "Price (number)", type: "number" },
              { key: "unit", label: "Unit (e.g. hr, flat)" },
              { key: "desc", label: "Description", type: "textarea" }
            ]} list={content.servicesList}
              onChange={(next) => { update({ servicesList: next }); ping(); }}
              defaultItem={{ num: "07", title: "New service", desc: "Describe this service...", price: 35, unit: "hr" }} />
          )}
          {tab === "payments-config" && (
            <PaymentSettingsEditor content={content} onChange={(partial) => { update(partial); ping(); }} />
          )}
          {tab === "payments" && (
            <PaymentsPanel />
          )}
          {tab === "faqs" && (
            <ListEditor title="FAQs" fields={[
              { key: "q", label: "Question" },
              { key: "a", label: "Answer", type: "textarea" }
            ]} list={content.faqs}
              onChange={(next) => { update({ faqs: next }); ping(); }}
              defaultItem={{ q: "New question?", a: "Answer..." }} />
          )}
          {tab === "testimonials" && (
            <ListEditor title="Testimonials" fields={[
              { key: "initial", label: "Initial" },
              { key: "name", label: "Name" },
              { key: "role", label: "Role" },
              { key: "text", label: "Quote", type: "textarea" }
            ]} list={content.testimonials}
              onChange={(next) => { update({ testimonials: next }); ping(); }}
              defaultItem={{ initial: "N", name: "New Name", role: "Family member", text: "They were wonderful..." }} />
          )}
          {tab === "roles" && (
            <ListEditor title="Open Roles" fields={[
              { key: "title", label: "Role title" },
              { key: "type", label: "Type / schedule" },
              { key: "desc", label: "Description", type: "textarea" },
              { key: "tagsText", label: "Tags (comma-separated)", mapIn: (item) => (item.tags || []).join(", "), mapOut: (val) => val.split(",").map(s => s.trim()).filter(Boolean) }
            ]} list={content.openRoles}
              onChange={(next) => { update({ openRoles: next }); ping(); }}
              defaultItem={{ title: "New Role", type: "Part-time · Flexible", desc: "Describe this role...", tags: ["Tag"] }} />
          )}
          {tab === "bookings" && (
            <BookingsPanel bookings={bookings} />
          )}
          {tab === "subscribers" && (
            <SubscribersPanel subs={subs} />
          )}
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="card-soft p-5">
      <div className="text-slate-400 text-xs uppercase tracking-widest">{label}</div>
      <div className="text-white font-serif-display text-3xl mt-1">{value}</div>
      {hint && <div className="text-slate-500 text-xs mt-1">{hint}</div>}
    </div>
  );
}

function OverviewPanel({ content, bookings, subs, setTab }) {
  const [payCount, setPayCount] = React.useState(0);
  React.useEffect(() => {
    try { setPayCount(JSON.parse(localStorage.getItem("3e_payments") || "[]").length); } catch {}
  }, []);
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Services" value={content.servicesList.length} />
        <StatCard label="Bookings" value={bookings.length} hint="Submitted via website" />
        <StatCard label="Payments" value={payCount} hint="Reported transactions" />
        <StatCard label="Subscribers" value={subs.length} hint="Newsletter signups" />
      </div>
      <div className="card-soft p-6">
        <h3 className="font-serif-display text-white text-xl mb-2">Quick edits</h3>
        <p className="text-slate-400 text-sm mb-4">Jump into the section you want to change.</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setTab("content")} className="btn-ghost text-sm !py-2">Edit hero & contact</button>
          <button onClick={() => setTab("payments-config")} className="btn-ghost text-sm !py-2">Edit payment methods</button>
          <button onClick={() => setTab("services")} className="btn-ghost text-sm !py-2">Edit services & prices</button>
          <button onClick={() => setTab("faqs")} className="btn-ghost text-sm !py-2">Edit FAQs</button>
          <button onClick={() => setTab("payments")} className="btn-ghost text-sm !py-2">Review payments</button>
          <button onClick={() => setTab("bookings")} className="btn-ghost text-sm !py-2">Review bookings</button>
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", textarea, rows = 3, placeholder }) {
  const Comp = textarea ? "textarea" : "input";
  const handle = (e) => {
    if (type === "number") onChange(e.target.value === "" ? "" : Number(e.target.value));
    else onChange(e.target.value);
  };
  return (
    <div>
      <label className="text-xs text-slate-400 mb-1 block">{label}</label>
      <Comp type={type} value={value === undefined || value === null ? "" : value} onChange={handle} rows={rows} placeholder={placeholder}
        className="w-full bg-[#0b1220] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#c7e34a]/60" />
    </div>
  );
}

function SiteInfoEditor({ content, onChange }) {
  const [draft, setDraft] = React.useState(content.siteInfo);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => { setDraft(content.siteInfo); setDirty(false); }, [content.siteInfo]);

  const change = (k, v) => { setDraft(d => ({ ...d, [k]: v })); setDirty(true); };

  const save = () => { onChange({ siteInfo: draft }); setDirty(false); };

  return (
    <div className="space-y-6">
      <div className="card-soft p-6">
        <h3 className="font-serif-display text-white text-xl mb-4">Brand & contact</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Brand name" value={draft.brand} onChange={v => change("brand", v)} />
          <TextField label="Tagline" value={draft.tagline} onChange={v => change("tagline", v)} />
          <TextField label="Phone (display)" value={draft.phone} onChange={v => change("phone", v)} />
          <TextField label="Phone (dial - digits only)" value={draft.phoneRaw} onChange={v => change("phoneRaw", v)} />
          <TextField label="CEO name" value={draft.ceo} onChange={v => change("ceo", v)} />
          <TextField label="CEO title" value={draft.ceoTitle} onChange={v => change("ceoTitle", v)} />
        </div>
        <div className="mt-4">
          <TextField label="CEO quote" value={draft.ceoQuote} onChange={v => change("ceoQuote", v)} textarea rows={3} />
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <TextField label="CEO photo URL" value={draft.ceoImage} onChange={v => change("ceoImage", v)} />
          <TextField label="Logo URL" value={draft.logo} onChange={v => change("logo", v)} />
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={save} disabled={!dirty} className={`${dirty ? "btn-lime" : "btn-ghost opacity-60"}`}><Save className="w-4 h-4" /> Save changes</button>
          {dirty && <button onClick={() => { setDraft(content.siteInfo); setDirty(false); }} className="btn-ghost">Cancel</button>}
        </div>
      </div>
    </div>
  );
}

function ListEditor({ title, fields, list, onChange, defaultItem }) {
  const [items, setItems] = React.useState(() => list.map(x => ({ ...x })));
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => { setItems(list.map(x => ({ ...x }))); setDirty(false); }, [list]);

  const updateItem = (i, patch) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    setDirty(true);
  };
  const remove = (i) => { setItems(prev => prev.filter((_, idx) => idx !== i)); setDirty(true); };
  const add = () => { setItems(prev => [...prev, { ...defaultItem }]); setDirty(true); };

  const save = () => {
    // Apply mapOut if provided to serialize fields
    const serialized = items.map(it => {
      const copy = { ...it };
      fields.forEach(f => {
        if (f.mapOut && copy[f.key] !== undefined) {
          const mapped = f.mapOut(copy[f.key]);
          // Replace into actual key (tags for tagsText case)
          if (f.key === "tagsText") copy.tags = mapped;
          else copy[f.key] = mapped;
          delete copy["tagsText"];
        }
      });
      return copy;
    });
    onChange(serialized);
    setDirty(false);
  };

  const getFieldValue = (item, f) => {
    if (f.mapIn) return f.mapIn(item);
    return item[f.key] ?? "";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-serif-display text-white text-2xl">{title}</h3>
        <div className="flex gap-2">
          <button onClick={add} className="btn-ghost text-sm"><Plus className="w-4 h-4" /> Add</button>
          <button onClick={save} disabled={!dirty} className={dirty ? "btn-lime text-sm" : "btn-ghost text-sm opacity-60"}>
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <div className="card-soft p-6 text-slate-400 text-sm">No items yet. Click Add.</div>}
        {items.map((item, i) => (
          <div key={i} className="card-soft p-5">
            <div className="flex items-start justify-between mb-3">
              <span className="text-xs text-slate-500 uppercase tracking-widest">Item #{i + 1}</span>
              <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-400 text-xs flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {fields.map(f => (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <TextField label={f.label} value={getFieldValue(item, f)}
                    onChange={(v) => {
                      if (f.key === "tagsText") updateItem(i, { tagsText: v });
                      else updateItem(i, { [f.key]: v });
                    }}
                    textarea={f.type === "textarea"} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BookingsPanel({ bookings }) {
  const { list, refresh, remove, clear } = bookings;
  const exportCSV = () => {
    const rows = [["Name", "Phone", "Email", "Date", "Time", "Type", "Note", "Submitted"]];
    list.forEach(b => rows.push([b.name, b.phone, b.email, b.date, b.time, b.tab, (b.note||"").replace(/\n/g, " "), new Date(b.at).toISOString()]));
    const csv = rows.map(r => r.map(c => `"${(c||"").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "3e-dancare-bookings.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-serif-display text-white text-2xl">Bookings <span className="text-slate-400 text-base font-sans">({list.length})</span></h3>
        <div className="flex gap-2">
          <button onClick={refresh} className="btn-ghost text-sm"><RefreshCcw className="w-4 h-4" /> Refresh</button>
          <button onClick={exportCSV} disabled={!list.length} className={list.length ? "btn-lime text-sm" : "btn-ghost text-sm opacity-60"}>Export CSV</button>
          {list.length > 0 && <button onClick={() => { if (confirm('Clear all bookings? This cannot be undone.')) clear(); }} className="btn-ghost text-sm text-red-300 hover:text-red-200">Clear all</button>}
        </div>
      </div>
      <p className="text-xs text-slate-500">Bookings are stored in this browser. Set up a backend to receive them server-side.</p>

      <div className="space-y-2">
        {list.length === 0 && <div className="card-soft p-6 text-slate-400 text-sm">No bookings yet.</div>}
        {list.map((b, i) => (
          <div key={i} className="card-soft p-5">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-white font-semibold">{b.name || "(unnamed)"}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#c7e34a]/15 text-[#c7e34a] border border-[#c7e34a]/30">{b.tab === "consult" ? "Consultation" : "Service"}</span>
                  {b.amount > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${b.paymentStatus === "reported" ? "bg-amber-500/10 border-amber-500/30 text-amber-300" : "bg-white/5 border-white/10 text-slate-300"}`}>
                      ${Number(b.amount).toFixed(2)} · {b.paymentStatus || "pending"}
                    </span>
                  )}
                  {b.id && <span className="text-[11px] text-slate-500">{b.id}</span>}
                </div>
                <div className="text-sm text-slate-300 mt-1 space-x-3">
                  <span>{b.phone}</span>
                  <span className="text-slate-400">•</span>
                  <span>{b.email}</span>
                </div>
                <div className="text-sm text-slate-400 mt-1">
                  <span>{(b.date || "").slice(0,10)}</span> <span className="mx-1">·</span> <span>{b.time}</span>
                  {b.service && <span className="ml-2 text-slate-500">· {b.service.title} ({b.service.hours}{b.service.unit})</span>}
                </div>
                {b.note && <p className="text-sm text-slate-300 mt-2 italic">“{b.note}”</p>}
                <div className="text-[11px] text-slate-500 mt-2">Submitted {new Date(b.at).toLocaleString()}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <a href={`tel:${b.phone}`} className="btn-ghost text-xs !py-1.5">Call</a>
                <a href={`mailto:${b.email}`} className="btn-ghost text-xs !py-1.5">Email</a>
                <button onClick={() => remove(i)} className="btn-ghost text-xs !py-1.5 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubscribersPanel({ subs }) {
  const { list, refresh, remove, clear } = subs;
  const exportCSV = () => {
    const rows = [["Email", "Submitted"]];
    list.forEach(s => rows.push([s.email, s.at ? new Date(s.at).toISOString() : ""]));
    const csv = rows.map(r => r.map(c => `"${(c||"").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "3e-dancare-subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-serif-display text-white text-2xl">Subscribers <span className="text-slate-400 text-base font-sans">({list.length})</span></h3>
        <div className="flex gap-2">
          <button onClick={refresh} className="btn-ghost text-sm"><RefreshCcw className="w-4 h-4" /> Refresh</button>
          <button onClick={exportCSV} disabled={!list.length} className={list.length ? "btn-lime text-sm" : "btn-ghost text-sm opacity-60"}>Export CSV</button>
          {list.length > 0 && <button onClick={() => { if (confirm('Clear all subscribers?')) clear(); }} className="btn-ghost text-sm text-red-300 hover:text-red-200">Clear all</button>}
        </div>
      </div>

      <div className="space-y-2">
        {list.length === 0 && <div className="card-soft p-6 text-slate-400 text-sm">No subscribers yet.</div>}
        {list.map((s, i) => (
          <div key={i} className="card-soft p-4 flex items-center justify-between">
            <div>
              <div className="text-white">{s.email}</div>
              {s.at && <div className="text-[11px] text-slate-500">Joined {new Date(s.at).toLocaleString()}</div>}
            </div>
            <div className="flex gap-2">
              <a href={`mailto:${s.email}`} className="btn-ghost text-xs !py-1.5">Email</a>
              <button onClick={() => remove(i)} className="btn-ghost text-xs !py-1.5 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentSettingsEditor({ content, onChange }) {
  const [draft, setDraft] = React.useState(content.paymentSettings || {});
  const [dirty, setDirty] = React.useState(false);
  React.useEffect(() => { setDraft(content.paymentSettings || {}); setDirty(false); }, [content.paymentSettings]);
  const change = (k, v) => { setDraft(d => ({ ...d, [k]: v })); setDirty(true); };
  const save = () => { onChange({ paymentSettings: draft }); setDirty(false); };

  return (
    <div className="space-y-6">
      <div className="card-soft p-6">
        <h3 className="font-serif-display text-white text-xl mb-1">Payment settings</h3>
        <p className="text-slate-400 text-sm mb-5">These values appear on the Booking and Services payment screens.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Consultation deposit (USD)" type="number" value={draft.consultationDeposit} onChange={v => change("consultationDeposit", Number(v) || 0)} />
          <TextField label="Currency code" value={draft.currency} onChange={v => change("currency", v)} />
          <TextField label="Cash App $cashtag" value={draft.cashapp} onChange={v => change("cashapp", v)} placeholder="$3EDanCare" />
          <TextField label="Cash App URL" value={draft.cashappUrl} onChange={v => change("cashappUrl", v)} />
          <TextField label="Zelle email" value={draft.zelleEmail} onChange={v => change("zelleEmail", v)} />
          <TextField label="Zelle phone" value={draft.zellePhone} onChange={v => change("zellePhone", v)} />
          <TextField label="Venmo handle" value={draft.venmo} onChange={v => change("venmo", v)} placeholder="@3EDanCare" />
          <TextField label="Venmo URL" value={draft.venmoUrl} onChange={v => change("venmoUrl", v)} />
          <TextField label="Bank account name" value={draft.bankName} onChange={v => change("bankName", v)} />
          <TextField label="Bank account number" value={draft.bankAccount} onChange={v => change("bankAccount", v)} />
          <TextField label="Routing number" value={draft.bankRouting} onChange={v => change("bankRouting", v)} />
          <div className="md:col-span-2">
            <TextField label="Bank instructions" value={draft.bankInstructions} onChange={v => change("bankInstructions", v)} textarea rows={3} />
          </div>
          <div className="md:col-span-2">
            <TextField label="Note shown after payment" value={draft.payNote} onChange={v => change("payNote", v)} textarea rows={2} />
          </div>
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={save} disabled={!dirty} className={dirty ? "btn-lime" : "btn-ghost opacity-60"}>
            <Save className="w-4 h-4" /> Save payment settings
          </button>
          {dirty && <button onClick={() => { setDraft(content.paymentSettings); setDirty(false); }} className="btn-ghost">Cancel</button>}
        </div>
      </div>
    </div>
  );
}

function PaymentsPanel() {
  const [list, setList] = React.useState([]);
  const refresh = React.useCallback(() => {
    try { setList(JSON.parse(localStorage.getItem("3e_payments") || "[]")); } catch { setList([]); }
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);
  const remove = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    localStorage.setItem("3e_payments", JSON.stringify(next));
    setList(next);
  };
  const clear = () => { localStorage.removeItem("3e_payments"); setList([]); };
  const mark = (idx, status) => {
    const next = list.map((p, i) => i === idx ? { ...p, status } : p);
    localStorage.setItem("3e_payments", JSON.stringify(next));
    setList(next);
  };
  const total = list.filter(p => p.status === "verified").reduce((a, p) => a + Number(p.amount || 0), 0);
  const pending = list.filter(p => p.status !== "verified").reduce((a, p) => a + Number(p.amount || 0), 0);

  const exportCSV = () => {
    const rows = [["Amount", "Currency", "Method", "Purpose", "Reference", "Name", "Phone", "Email", "BookingID", "Status", "At"]];
    list.forEach(p => rows.push([
      p.amount, p.currency, p.method, p.purpose, p.reference,
      p.meta?.name || "", p.meta?.phone || "", p.meta?.email || "",
      p.bookingId || p.meta?.bookingId || "", p.status, new Date(p.at).toISOString()
    ]));
    const csv = rows.map(r => r.map(c => `"${(c==null?"":String(c)).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "3e-dancare-payments.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-serif-display text-white text-2xl">Payments <span className="text-slate-400 text-base font-sans">({list.length})</span></h3>
        <div className="flex gap-2">
          <button onClick={refresh} className="btn-ghost text-sm"><RefreshCcw className="w-4 h-4" /> Refresh</button>
          <button onClick={exportCSV} disabled={!list.length} className={list.length ? "btn-lime text-sm" : "btn-ghost text-sm opacity-60"}>Export CSV</button>
          {list.length > 0 && <button onClick={() => { if (confirm('Clear all payments?')) clear(); }} className="btn-ghost text-sm text-red-300 hover:text-red-200">Clear all</button>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Verified total" value={`$${total.toFixed(2)}`} hint="Marked as verified" />
        <StatCard label="Pending review" value={`$${pending.toFixed(2)}`} hint="Reported, not yet verified" />
        <StatCard label="Records" value={list.length} />
      </div>

      <p className="text-xs text-slate-500">Customers report payments via Cash App, Zelle, Venmo, or bank transfer. Verify by checking your account, then mark verified below.</p>

      <div className="space-y-2">
        {list.length === 0 && <div className="card-soft p-6 text-slate-400 text-sm">No payments reported yet.</div>}
        {list.slice().reverse().map((p, revIdx) => {
          const i = list.length - 1 - revIdx;
          return (
            <div key={i} className="card-soft p-5">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-white font-semibold">${Number(p.amount||0).toFixed(2)} {p.currency || "USD"}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-200 capitalize">{p.method}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${p.status === "verified" ? "bg-[#c7e34a]/15 border-[#c7e34a]/30 text-[#c7e34a]" : p.status === "rejected" ? "bg-red-500/10 border-red-500/30 text-red-300" : "bg-amber-500/10 border-amber-500/30 text-amber-300"}`}>{p.status}</span>
                  </div>
                  <div className="text-sm text-slate-300 mt-1">{p.purpose}</div>
                  {p.meta?.name && <div className="text-sm text-slate-400 mt-1">{p.meta.name} · {p.meta.phone} · {p.meta.email}</div>}
                  {p.reference && <div className="text-xs text-slate-400 mt-1">Ref: {p.reference}</div>}
                  {(p.bookingId || p.meta?.bookingId) && <div className="text-xs text-slate-500 mt-1">Booking: {p.bookingId || p.meta?.bookingId}</div>}
                  <div className="text-[11px] text-slate-500 mt-2">{new Date(p.at).toLocaleString()}</div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {p.status !== "verified" && <button onClick={() => mark(i, "verified")} className="btn-lime text-xs !py-1.5">Mark verified</button>}
                  {p.status !== "rejected" && <button onClick={() => mark(i, "rejected")} className="btn-ghost text-xs !py-1.5 text-red-300">Reject</button>}
                  <button onClick={() => remove(i)} className="btn-ghost text-xs !py-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

